/**
 * Edição de UM passo no editor de manuais: título curto (vai para o índice),
 * sistema, texto em markdown curto (botões B e `código`), aviso, trecho para
 * copiar, seção e a referência a passos de outra tarefa ("passos 15 a 25 de
 * Otimizar lote") — escolhida por passo, nunca digitada como número.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiBold, FiCode } from "react-icons/fi";
import styled from "styled-components";

import { useAuth } from "../../context/AuthContext";
import { lerTarefa } from "../../services/areaPcp";
import { Aviso, Campo, Grade2 } from "./styles";

const Barra = styled.div`
  display: flex;
  gap: 4px;

  button {
    width: 36px;
    height: 32px;
    border-radius: 7px;
    border: 1px solid var(--border);
    background: var(--surface-2);
    color: var(--ink-2);
    display: grid;
    place-items: center;
    cursor: pointer;
  }
`;

const Caixa = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-top: 1px solid var(--border);
  background: var(--bg);
`;

/** Envolve a seleção do textarea com a marca (ex.: ** para negrito). */
function envolver(textarea, marca, valor, onMudar) {
  if (!textarea) return;
  const { selectionStart: inicio, selectionEnd: fim } = textarea;
  const selecionado = valor.slice(inicio, fim) || "texto";
  onMudar(`${valor.slice(0, inicio)}${marca}${selecionado}${marca}${valor.slice(fim)}`);
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(inicio + marca.length, inicio + marca.length + selecionado.length);
  });
}

function usePassosDaTarefa(tarefaId) {
  const { request } = useAuth();
  const [passos, setPassos] = useState([]);
  useEffect(() => {
    if (!tarefaId) {
      setPassos([]);
      return undefined;
    }
    let ativo = true;
    lerTarefa(request, tarefaId).then((t) => ativo && setPassos(t.passos)).catch(() => ativo && setPassos([]));
    return () => {
      ativo = false;
    };
  }, [request, tarefaId]);
  return passos;
}

function Referencia({ referencia, outrasTarefas, onMudar }) {
  const passos = usePassosDaTarefa(referencia?.tarefa_id);
  const rotuloDoPasso = (p) => `${p.numero}. ${p.titulo_curto || p.texto.slice(0, 50)}`;

  if (!referencia) {
    return (
      <button
        type="button"
        onClick={() => onMudar({ tarefa_id: "", inicio: "", fim: "" })}
        style={{ alignSelf: "flex-start", background: "none", border: 0, color: "var(--accent)", font: "inherit", cursor: "pointer", padding: 0 }}
      >
        + Referenciar passos de outra tarefa (ex.: passos 15 a 25 da Tarefa 1)
      </button>
    );
  }
  return (
    <Aviso $tom="info" style={{ flexDirection: "column", alignItems: "stretch" }}>
      <strong>Sub-checklist de outra tarefa</strong>
      <Campo>
        <span>Tarefa</span>
        <select
          value={referencia.tarefa_id}
          onChange={(e) => onMudar({ tarefa_id: e.target.value, inicio: "", fim: "" })}
        >
          <option value="">Escolha…</option>
          {outrasTarefas.map((t) => (
            <option key={t.id} value={t.id}>{t.titulo}</option>
          ))}
        </select>
      </Campo>
      {referencia.tarefa_id ? (
        <Grade2>
          <Campo>
            <span>Do passo</span>
            <select value={referencia.inicio} onChange={(e) => onMudar({ ...referencia, inicio: e.target.value })}>
              <option value="">Escolha…</option>
              {passos.map((p) => <option key={p.chave} value={p.chave}>{rotuloDoPasso(p)}</option>)}
            </select>
          </Campo>
          <Campo>
            <span>Até o passo</span>
            <select value={referencia.fim} onChange={(e) => onMudar({ ...referencia, fim: e.target.value })}>
              <option value="">Escolha…</option>
              {passos.map((p) => <option key={p.chave} value={p.chave}>{rotuloDoPasso(p)}</option>)}
            </select>
          </Campo>
        </Grade2>
      ) : null}
      <button
        type="button"
        onClick={() => onMudar(null)}
        style={{ alignSelf: "flex-start", background: "none", border: 0, color: "var(--danger-text)", font: "inherit", cursor: "pointer", padding: 0 }}
      >
        Tirar a referência
      </button>
    </Aviso>
  );
}

export default function EditorPasso({ passo, numero, secoes, sistemas, outrasTarefas, alertaDeNumero, onCampo }) {
  const texto = useRef(null);
  const aviso = useRef(null);
  const idSistemas = `sistemas-${passo.chave}`;

  return (
    <Caixa>
      <Grade2>
        <Campo>
          <span>Título curto <span className="dica">(aparece no índice e no topo do passo)</span></span>
          <input value={passo.titulo_curto} maxLength={80} onChange={(e) => onCampo("titulo_curto", e.target.value)} />
        </Campo>
        <Campo>
          <span>Sistema</span>
          <input
            value={passo.sistema}
            maxLength={40}
            list={idSistemas}
            placeholder="Pharu ERP, PharuCut, Dinabox…"
            onChange={(e) => onCampo("sistema", e.target.value)}
          />
          <datalist id={idSistemas}>
            {sistemas.map((s) => <option key={s} value={s} />)}
          </datalist>
        </Campo>
      </Grade2>
      <Campo as="div">
        <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Texto do passo {numero}
          <Barra>
            <button type="button" aria-label="Negrito" onClick={() => envolver(texto.current, "**", passo.texto, (v) => onCampo("texto", v))}>
              <FiBold size={14} aria-hidden="true" />
            </button>
            <button type="button" aria-label="Código ou caminho de pasta" onClick={() => envolver(texto.current, "`", passo.texto, (v) => onCampo("texto", v))}>
              <FiCode size={14} aria-hidden="true" />
            </button>
          </Barra>
        </span>
        <textarea
          ref={texto}
          aria-label={`Texto do passo ${numero}`}
          value={passo.texto}
          maxLength={2000}
          onChange={(e) => onCampo("texto", e.target.value)}
        />
      </Campo>
      {alertaDeNumero ? (
        <Aviso $tom="warning">
          O texto cita “{alertaDeNumero}” escrito à mão. Esse número não acompanha quando os passos mudam de lugar.
        </Aviso>
      ) : null}
      <Campo>
        <span>Aviso <span className="dica">(opcional, aparece em destaque com ATENÇÃO)</span></span>
        <textarea ref={aviso} value={passo.aviso} maxLength={600} onChange={(e) => onCampo("aviso", e.target.value)} />
      </Campo>
      <Grade2>
        <Campo>
          <span>Trecho para copiar <span className="dica">(ex.: um caminho de pasta)</span></span>
          <input
            value={passo.trecho_copiavel}
            maxLength={300}
            style={{ fontFamily: "var(--font-mono)" }}
            onChange={(e) => onCampo("trecho_copiavel", e.target.value)}
          />
        </Campo>
        <Campo>
          <span>Seção</span>
          <select value={passo.secao || ""} onChange={(e) => onCampo("secao", e.target.value || null)}>
            <option value="">Sem seção</option>
            {secoes.map((s, i) => <option key={s.chave} value={s.chave}>{i + 1}. {s.titulo || "(sem título)"}</option>)}
          </select>
        </Campo>
      </Grade2>
      <Referencia
        referencia={passo.referencia}
        outrasTarefas={outrasTarefas}
        onMudar={(valor) => onCampo("referencia", valor)}
      />
    </Caixa>
  );
}
